import getConfig from '../../app/config';
import fetchWithRefresh from '../../services/fetchWithRefresh';
import { computeOptimalResolutionsForTrackTypes } from './resolutionHelpers';

export interface ViewTrackBoreholeRequest {
  boreholeId: number;
  projectId: string;
  trackTypeIds: number[];
  imageTypeIds: number[];
}

interface FetchViewTrackDataParams {
  clientId: string;
  boreholes: ViewTrackBoreholeRequest[];
  startDepth: number;
  endDepth: number;
  pixelHeight: number;
  depthUnit?: number | null;
  existingScaleMetadataByBorehole?: { [boreholeId: string]: any[] };
  existingGroupTrackImages?: any[];
  existingGroupTrackData?: any[];
  cumulativeGroups?: number[][];
  /** When true, skip the getScaleMetadata API call and reuse existingScaleMetadataByBorehole.
   *  Use this when panning / making minor depth range adjustments where the optimal scale
   *  resolution is unlikely to have changed. */
  skipScaleCheck?: boolean;
}

const mergeScaleMetadataByBorehole = (
  base: { [boreholeId: string]: any[] },
  incomingScales: any[]
) => {
  const merged: { [boreholeId: string]: any[] } = { ...base };
  (incomingScales || []).forEach((item: any) => {
    const bhId = String(item?.boreholeId);
    if (!bhId) return;
    const existing = merged[bhId] || [];
    const byId: { [id: number]: any } = {};
    existing.forEach((m: any) => { byId[m.trackTypeId] = m; });
    if (typeof item?.trackTypeId === 'number') {
      byId[item.trackTypeId] = item;
    }
    merged[bhId] = Object.values(byId);
  });
  return merged;
};

const mergeGroupTrackImages = (base: any[], incoming: any[]) => {
  const merged = base.slice();
  const existingKeys = new Set(
    merged.map((ti: any) => `${ti?.boreholeId || ''}_${ti?.imageType || ''}_${ti?.id || ''}`)
  );
  (incoming || []).forEach((ti: any) => {
    const key = `${ti?.boreholeId || ''}_${ti?.imageType || ''}_${ti?.id || ''}`;
    if (!existingKeys.has(key)) {
      merged.push(ti);
      existingKeys.add(key);
    }
  });
  return merged;
};

export const fetchViewScalesAndTrackDataDirect = async (params: FetchViewTrackDataParams) => {
  const {
    clientId,
    boreholes,
    startDepth,
    endDepth,
    pixelHeight,
    depthUnit,
    existingScaleMetadataByBorehole = {},
    existingGroupTrackImages = [],
    existingGroupTrackData = [],
    cumulativeGroups,
    skipScaleCheck = false
  } = params;

  if (!clientId || !boreholes || boreholes.length === 0) {
    return {
      scaleMetadataByBorehole: existingScaleMetadataByBorehole,
      groupTrackImages: existingGroupTrackImages,
      groupTrackData: existingGroupTrackData,
      scalesLoaded: false,
      minimumDepth: null as number | null,
      maximumDepth: null as number | null
    };
  }

  const depthRange = endDepth - startDepth;
  if (depthRange <= 0) {
    return {
      scaleMetadataByBorehole: existingScaleMetadataByBorehole,
      groupTrackImages: existingGroupTrackImages,
      groupTrackData: existingGroupTrackData,
      scalesLoaded: false,
      minimumDepth: null as number | null,
      maximumDepth: null as number | null
    };
  }

  const apiUrl = getConfig().apiUrl;

  let mergedScaleByBorehole: { [boreholeId: string]: any[] };
  let mergedImages: any[];
  let scalesMinDepth: number | null = null;
  let scalesMaxDepth: number | null = null;

  if (skipScaleCheck) {
    // Reuse existing scale metadata — no network call needed
    mergedScaleByBorehole = existingScaleMetadataByBorehole;
    mergedImages = existingGroupTrackImages;
    // Derive depth extents from existing metadata
    Object.values(existingScaleMetadataByBorehole).forEach((items: any[]) => {
      (items || []).forEach((item: any) => {
        if (typeof item.minimumDepth === 'number') {
          if (scalesMinDepth === null || item.minimumDepth < scalesMinDepth) scalesMinDepth = item.minimumDepth;
        }
        if (typeof item.maximumDepth === 'number') {
          if (scalesMaxDepth === null || item.maximumDepth > scalesMaxDepth) scalesMaxDepth = item.maximumDepth;
        }
      });
    });
  } else {
    const scalesResp: any = await fetchWithRefresh(
      `${apiUrl}client/${clientId}/trackData/scales`,
      undefined,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ boreholes, byBoreholeDepth: true })
      }
    );

    const incomingScales = Array.isArray(scalesResp?.trackDataScales)
      ? scalesResp.trackDataScales
      : [];
    mergedScaleByBorehole = mergeScaleMetadataByBorehole(
      existingScaleMetadataByBorehole,
      incomingScales
    );
    mergedImages = Array.isArray(scalesResp?.trackImages)
      ? mergeGroupTrackImages(existingGroupTrackImages, scalesResp.trackImages)
      : existingGroupTrackImages;

    // Derive overall depth extent from the scales response
    incomingScales.forEach((item: any) => {
      if (typeof item.minimumDepth === 'number') {
        if (scalesMinDepth === null || item.minimumDepth < scalesMinDepth) scalesMinDepth = item.minimumDepth;
      }
      if (typeof item.maximumDepth === 'number') {
        if (scalesMaxDepth === null || item.maximumDepth > scalesMaxDepth) scalesMaxDepth = item.maximumDepth;
      }
    });
  }

  const boreholeEntries: Array<{ boreholeId: number; projectId: string; trackTypeEntries: Array<{ trackTypeId: number; scale: number }> }> = [];
  boreholes.forEach((b) => {
    const metaArr = mergedScaleByBorehole[String(b.boreholeId)] || [];
    const metaMap: { [trackTypeId: number]: any } = {};
    metaArr.forEach((m: any) => { metaMap[m.trackTypeId] = m; });
    const available = (b.trackTypeIds || []).filter((id) => metaMap[id]);
    if (available.length === 0) return;
    const entries = computeOptimalResolutionsForTrackTypes(
      available,
      metaMap as any,
      depthRange,
      2000,
      cumulativeGroups
    );
    if (entries.length > 0) {
      boreholeEntries.push({
        boreholeId: b.boreholeId,
        projectId: b.projectId,
        trackTypeEntries: entries
      });
    }
  });

  if (boreholeEntries.length === 0) {
    return {
      scaleMetadataByBorehole: mergedScaleByBorehole,
      groupTrackImages: mergedImages,
      groupTrackData: existingGroupTrackData,
      scalesLoaded: true,
      minimumDepth: scalesMinDepth,
      maximumDepth: scalesMaxDepth
    };
  }

  const dataResp: any = await fetchWithRefresh(
    `${apiUrl}client/${clientId}/trackData/multiBorehole`,
    undefined,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        clientId,
        boreholeEntries,
        pixelHeight,
        byBoreholeDepth: true,
        startDepth,
        endDepth,
        depthUnit: depthUnit ?? null
      })
    }
  );

  const scaleMap: { [boreholeId: string]: { [trackTypeId: number]: number } } = {};
  boreholeEntries.forEach((bhEntry) => {
    const bhId = String(bhEntry.boreholeId);
    scaleMap[bhId] = {};
    bhEntry.trackTypeEntries.forEach((ttEntry) => {
      scaleMap[bhId][ttEntry.trackTypeId] = ttEntry.scale;
    });
  });

  const incomingTrackData = Array.isArray(dataResp?.trackData) ? dataResp.trackData : [];
  const mergedTrackData = existingGroupTrackData.slice();
  incomingTrackData.forEach((item: any) => {
    const bId = typeof item?.boreholeId === 'string' ? parseInt(item.boreholeId) : item.boreholeId;
    const ttId = item?.trackTypeId;
    if (isNaN(bId) || typeof ttId !== 'number') return;
    const bhIdStr = String(item.boreholeId);
    let scale = scaleMap[bhIdStr]?.[ttId];
    if (typeof scale !== 'number' && typeof item?.scale === 'number') {
      scale = item.scale;
    }
    const rawDataArray = Array.isArray(item?.data) ? item.data : [];
    const nestedData = rawDataArray.length > 0 && rawDataArray[0] && Array.isArray(rawDataArray[0].data)
      ? rawDataArray[0].data
      : rawDataArray;

    const normalized = {
      ...item,
      boreholeId: bId,
      trackTypeId: ttId,
      scale,
      data: nestedData,
      noData: item?.noData ?? (nestedData.length === 0)
    };

    const existingIndex = mergedTrackData.findIndex((t: any) => t.boreholeId === bId && t.trackTypeId === ttId);
    if (existingIndex >= 0) {
      mergedTrackData[existingIndex] = normalized;
    } else {
      mergedTrackData.push(normalized);
    }
  });

  return {
    scaleMetadataByBorehole: mergedScaleByBorehole,
    groupTrackImages: mergedImages,
    groupTrackData: mergedTrackData,
    scalesLoaded: true,
    minimumDepth: scalesMinDepth,
    maximumDepth: scalesMaxDepth
  };
};
